import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { SessionExpiredHandlerService } from '../services/session-expired-handler.service';

/**
 * Intercepta respuestas 401 para limpiar tokens y redirigir al login del
 * contexto. Evita el caso (frecuente en celular) en que el JWT expira en
 * background y el usuario queda viendo errores genericos sin entender que
 * tiene que volver a iniciar sesion.
 *
 * Filtra requests que legitimamente pueden dar 401 sin sesion expirada:
 * endpoints de login (la falla es credenciales malas, no token muerto) y
 * endpoints publicos.
 *
 * SessionExpiredHandlerService se resuelve LAZY via Injector — no eager
 * con inject() — porque el handler depende de TranslateService, y este
 * usa HttpClient (a traves de provideTranslateHttpLoader) para cargar
 * es.json en APP_INITIALIZER. Eager-inject en cada request del interceptor
 * dispara NG0200 circular dependency cuando la primera request es la del
 * loader de translate. Lazy resolution corre solo en el 401, momento en
 * el que el grafo DI ya esta estabilizado.
 */
export const sessionExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const router = inject(Router);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401 && shouldHandle(req.url)) {
        injector.get(SessionExpiredHandlerService).handle(router.url);
      }
      return throwError(() => err);
    })
  );
};

function shouldHandle(url: string): boolean {
  const lower = url.toLowerCase();
  const exempt = [
    '/auth/login',
    '/restaurant/auth/login',
    '/superadmin/auth/login',
    '/kiosk/login',
    '/api/public/',
    '/api/menu/',
    '/auth/customer/login',
    '/auth/customer/register',
    '/auth/customer/refresh'
  ];
  return !exempt.some(fragment => lower.includes(fragment));
}
